import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { useRecordOverallLlmEventMutation } from '~/hooks/useRecordOverallLlmEventMutation'
import { compile as compileRootPath } from '~/routes/paths'

type MatchedSection = {
  documentTitle: string
  excerpt: string
  reasoning: string
  restoreKey: string
  sectionNumber: number
}

type DocumentQaResultsProps = {
  brandAccountId: string
  requestId?: string
  results: {
    error?: string
    errorI18nKey?:
      | 'ai_disabled_message'
      | 'an_error_has_occurred'
      | 'document_qa_no_candidates'
      | 'document_qa_request_not_found'
      | 'document_qa_timeout'
    matchedSections: MatchedSection[]
  }
}

export const DocumentQaResults: React.FC<DocumentQaResultsProps> = ({
  brandAccountId,
  requestId,
  results,
}) => {
  const { t } = useTranslation()
  const recordLlmEventMutation = useRecordOverallLlmEventMutation()

  const handleResultClick = (section: MatchedSection, index: number) => {
    // Fire-and-forget: navigation must proceed even on logging failure.
    recordLlmEventMutation
      .mutateAsync({
        brandAccountId,
        kind: 'reaction.document_qa.result',
        payload: JSON.stringify({
          index,
          requestId,
          restoreKey: section.restoreKey,
          sectionNumber: section.sectionNumber,
          targetId: section.restoreKey,
        }),
      })
      .catch(() => {
        // Intentionally swallowed — telemetry failure must not affect UX.
      })
  }

  // Translate at render time so a language switch is reflected immediately;
  // `error` carries backend-provided text and is shown verbatim.
  if (results.errorI18nKey) {
    return <div style={{ color: 'red' }}>{t(results.errorI18nKey)}</div>
  }

  if (results.error) {
    return <div style={{ color: 'red' }}>{results.error}</div>
  }

  if (results.matchedSections.length === 0) {
    return <div>{t('document_qa_no_results')}</div>
  }

  return (
    <div
      style={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        gap: '16px',
        overflowY: 'auto',
      }}
    >
      {results.matchedSections.map((section, index) => {
        const link = compileRootPath('restore', { key: section.restoreKey })

        return (
          <Link
            key={`${section.restoreKey}-${section.sectionNumber}`}
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'none' }}
            target="_blank"
            to={link}
            onClick={() => handleResultClick(section, index)}
          >
            <div
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '16px',
              }}
            >
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '8px',
                }}
              >
                {section.documentTitle}
              </div>
              <div style={{ marginBottom: '8px' }}>{section.excerpt}</div>
              <div style={{ color: '#888', fontSize: '12px' }}>
                {section.reasoning}
              </div>
            </div>
          </Link>
        )
      })}
    </div>
  )
}
